import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/sections/Navigation';
import Footer from '@/components/sections/Footer';
import { ArrowLeft } from 'lucide-react';
import { BRAND } from '@/lib/brand';

const H2 = ({ children }) => (
  <h2 className="font-serif text-[#0E2238] text-2xl md:text-3xl mt-12 mb-4 tracking-tight">{children}</h2>
);
const P = ({ children }) => (
  <p className="text-[#2B2B2B]/80 text-base md:text-[1.05rem] leading-[1.75] font-light mb-4">{children}</p>
);

export default function Privacy() {
  useEffect(() => {
    document.title = 'Privacy Policy — Bowers Advisory Group';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F4EE]">
      <Navigation />
      <main className="pt-40 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-[#B68A35] hover:text-[#0E2238] text-sm tracking-wide mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <div className="overline text-[#B68A35] mb-6">Privacy</div>
          <h1 className="font-serif text-[#0E2238] text-4xl md:text-5xl leading-[1.05] tracking-tight mb-4">
            Privacy Policy
          </h1>
          <p className="text-[#2B2B2B]/55 text-sm tracking-wide">Effective date: July 2026 &middot; Last updated: July 2026</p>

          <div className="mt-10">
            <P>
              Bowers Advisory Group ("we," "us," or "our") respects your privacy. This Privacy Policy
              explains what information we collect through {BRAND.website}, how we use it, and the
              choices you have. By using this site, you agree to the practices described below.
            </P>

            <H2>1. Information We Collect</H2>
            <P>
              <strong>Information you provide.</strong> When you submit a consultation request, request
              the Legacy Readiness Checklist, or otherwise contact us, we collect the information you
              share: name, email address, phone number, area of interest, and any message content.
            </P>
            <P>
              <strong>Automatically collected information.</strong> We may collect standard technical
              information (IP address, browser type, pages visited, referring URL, and timestamps) to
              maintain and improve the site.
            </P>

            <H2>2. How We Use Your Information</H2>
            <P>We use the information you provide to:</P>
            <ul className="list-disc pl-6 space-y-2 mb-4 text-[#2B2B2B]/80 text-base md:text-[1.05rem] font-light leading-[1.7]">
              <li>Respond to your inquiry and follow up personally regarding your goals;</li>
              <li>Provide the requested educational resources, such as the Legacy Readiness Checklist;</li>
              <li>Maintain records for regulatory, compliance, and operational purposes;</li>
              <li>Improve our site content and user experience.</li>
            </ul>
            <P>We do not sell, rent, or trade your personal information.</P>

            <H2>3. How We Share Your Information</H2>
            <P>
              We share information only with (a) service providers that help us operate the site and
              communicate with you (such as email and hosting providers), under confidentiality
              obligations, and (b) as required by law, regulation, subpoena, or to protect the rights,
              property, or safety of Bowers Advisory Group or others.
            </P>

            <H2>4. Data Retention</H2>
            <P>
              We retain the information you provide for as long as reasonably necessary to fulfill the
              purposes described in this Policy and to comply with legal, regulatory, and record-keeping
              obligations applicable to insurance and financial consulting services.
            </P>

            <H2>5. Security</H2>
            <P>
              We use reasonable administrative, technical, and physical safeguards to protect the
              information you share with us. No online transmission or storage is ever completely
              secure, and we cannot guarantee absolute security.
            </P>

            <H2>6. Your Choices</H2>
            <P>
              You may request to review, correct, or delete personal information we hold about you by
              contacting us at <a className="text-[#B68A35] hover:underline" href={BRAND.emailHref}>{BRAND.email}</a>.
              You may also opt out of future email communications from us by replying with "unsubscribe."
              We will honor reasonable requests promptly, subject to any legal or regulatory
              record-keeping requirements.
            </P>

            <H2>7. Third-Party Links</H2>
            <P>
              This site may include links to third-party websites. We are not responsible for the
              privacy practices or content of those sites and encourage you to review their policies.
            </P>

            <H2>8. Children's Privacy</H2>
            <P>
              This site is not directed to children under 13, and we do not knowingly collect personal
              information from children under 13.
            </P>

            <H2>9. Changes to This Policy</H2>
            <P>
              We may update this Policy from time to time. When we do, we will revise the "Last updated"
              date above. Material changes will be reflected on this page.
            </P>

            <H2>10. Contact Us</H2>
            <P>
              Questions about this Privacy Policy or our data practices can be sent to{' '}
              <a className="text-[#B68A35] hover:underline" href={BRAND.emailHref}>{BRAND.email}</a>{' '}
              or by phone at{' '}
              <a className="text-[#B68A35] hover:underline" href={BRAND.phoneHref}>{BRAND.phone}</a>.
            </P>

            <div className="mt-16 p-6 border border-[#0E2238]/10 bg-white">
              <p className="text-[#2B2B2B]/70 text-xs leading-relaxed">
                This Privacy Policy is provided for general informational purposes and does not constitute
                legal advice. Bowers Advisory Group provides financial education, insurance-based strategies,
                and consulting services. Services and product availability may vary by license, carrier, and
                state requirements.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
